import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Dog — a stubby happy puppy, storybook style: a round little body on four
// stubby legs, an oversized head with floppy ears, a big cream snout with a
// dark nose ball, and a stumpy upright tail. Faces +Z, stands on y = 0.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body" : the torso. Breathes in idle; wiggles its butt (a yaw about the
//              chest) during the excited tail-wag.
//   - "head" : head + ears + snout + eyes. Bobs and tilts about the neck.
//   - "tail" : the stumpy tail. Wags fast about its root.
// The four planted legs and paws are static (unnamed).

const COAT = "#c98850";       // warm caramel coat
const COAT_DARK = "#a56a38";  // shaded ears / saddle
const CREAM = "#f6efe3";      // snout, belly, paws, tail tip
const NOSE = "#4a342a";       // the big nose ball (dark brown, never black)
const EYE_WHITE = "#f7f3e8";  // eye whites (never pure white)
const PUPIL = "#33261c";      // warm dark pupils
const TONGUE = "#e08c8c";     // a happy little tongue

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0.20, 0.12];   // the chest — so the butt swings
const HEAD_PIVOT: [number, number, number] = [0, 0.30, 0.10];   // the neck
const TAIL_PIVOT: [number, number, number] = [0, 0.27, -0.16];  // tail root

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.037, 10, 8), color: EYE_WHITE,
      position: [x, 0.445, 0.255], name: "head" },
    { geometry: new THREE.SphereGeometry(0.019, 8, 6), color: PUPIL,
      position: [x, 0.445, 0.284], name: "head" },
    { geometry: new THREE.SphereGeometry(0.007, 6, 5), color: CREAM,
      position: [x + 0.008, 0.455, 0.297], name: "head" },
  ];
}

// One floppy ear: a flattened drop hanging down the side of the head.
function flopEar(side: 1 | -1): Part {
  return {
    geometry: new THREE.SphereGeometry(0.065, 8, 8), color: COAT_DARK,
    position: [side * 0.145, 0.375, 0.06],
    rotation: [0, 0, -side * 0.28],
    scale: [0.35, 1.4, 0.85], name: "head",
  };
}

// One stubby leg with a cream paw.
function leg(x: number, z: number): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.042, 0.045, 0.15, 8), color: COAT,
      position: [x, 0.075, z] },
    { geometry: new THREE.SphereGeometry(0.05, 8, 6), color: CREAM,
      position: [x, 0.04, z + 0.02], scale: [1, 0.7, 1.2] },
  ];
}

export const dog = defineModel({
  id: "dog",
  batchId: "mess",
  name: "Dog",
  tags: ["mess", "cute", "animated", "creature", "animal", "dog", "puppy", "pet", "mammal"],
  summary: "A stubby happy puppy with floppy ears, a big snout and a fast-wagging tail.",
  description:
    "A storybook puppy: a round caramel body on four stubby legs, an oversized " +
    "head with floppy dark ears, a big cream snout with a chunky nose ball and " +
    "a happy tongue. Breathes at rest (idle) and does an excited tail-wag with " +
    "a little butt wiggle (tail-wag).",
  author: "models",
  footprint: 0.3,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── body (named) ─────────────────────────────────────────────────────
      { geometry: new THREE.SphereGeometry(0.13, 12, 10), color: COAT,
        position: [0, 0.21, -0.02], scale: [1.0, 0.92, 1.35], name: "body" },
      // Cream belly.
      { geometry: new THREE.SphereGeometry(0.095, 10, 8), color: CREAM,
        position: [0, 0.16, 0.0], scale: [0.95, 0.75, 1.25], name: "body" },
      // A darker saddle patch on the back.
      { geometry: new THREE.SphereGeometry(0.095, 10, 8), color: COAT_DARK,
        position: [0, 0.27, -0.07], scale: [1.05, 0.6, 1.1], name: "body" },

      // ── legs + paws (static, planted) ────────────────────────────────────
      ...leg(-0.075, 0.10), ...leg(0.075, 0.10),
      ...leg(-0.075, -0.12), ...leg(0.075, -0.12),

      // ── head (named) ─────────────────────────────────────────────────────
      { geometry: new THREE.SphereGeometry(0.15, 14, 12), color: COAT,
        position: [0, 0.40, 0.14], name: "head" },
      flopEar(1), flopEar(-1),
      // Big cream snout.
      { geometry: new THREE.SphereGeometry(0.075, 10, 8), color: CREAM,
        position: [0, 0.355, 0.245], scale: [1.15, 0.8, 1.0], name: "head" },
      // The chunky nose ball on the tip.
      { geometry: new THREE.SphereGeometry(0.035, 8, 6), color: NOSE,
        position: [0, 0.378, 0.315], name: "head" },
      // A happy little tongue poking out under the snout.
      { geometry: new THREE.BoxGeometry(0.045, 0.012, 0.05), color: TONGUE,
        position: [0.012, 0.315, 0.30], rotation: [0.25, 0, 0], name: "head" },
      ...eye(0.06), ...eye(-0.06),

      // ── tail (named, wags) ───────────────────────────────────────────────
      { geometry: new THREE.CylinderGeometry(0.02, 0.028, 0.13, 8), color: COAT,
        position: [0, 0.31, -0.21], rotation: [-0.7, 0, 0], name: "tail" },
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: CREAM,
        position: [0, 0.36, -0.25], name: "tail" },
    ];
  },
  animations: [
    // Idle — a soft breath: the body swells, the head bobs, the tail sways lazily.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 2.8,
      loop: true,
      tracks: [
        {
          target: "body", pivot: [0, 0, 0],
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.4, value: [1.02, 1.04, 1.01], ease: "sine" },
            { t: 2.8, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.012, 0], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.03, 0, 0], ease: "sine" },
            { t: 1.4, value: [-0.03, 0, 0.02], ease: "sine" },
            { t: 2.8, value: [0.03, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0.15, 0], ease: "sine" },
            { t: 1.4, value: [0, -0.15, 0], ease: "sine" },
            { t: 2.8, value: [0, 0.15, 0], ease: "sine" },
          ],
        },
      ],
    },

    // Tail-wag — the signature move: a fast, excited wag with the whole butt
    // wiggling along (the body yaws about the chest) and the head cocked happy.
    {
      id: "tail-wag",
      label: "Excited tail wag",
      duration: 1.2,
      loop: true,
      tracks: [
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.15, value: [0, 0.7, 0], ease: "sine" },
            { t: 0.45, value: [0, -0.7, 0], ease: "sine" },
            { t: 0.75, value: [0, 0.7, 0], ease: "sine" },
            { t: 1.05, value: [0, -0.7, 0], ease: "sine" },
            { t: 1.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.15, value: [0, -0.09, 0], ease: "sine" },
            { t: 0.45, value: [0, 0.09, 0], ease: "sine" },
            { t: 0.75, value: [0, -0.09, 0], ease: "sine" },
            { t: 1.05, value: [0, 0.09, 0], ease: "sine" },
            { t: 1.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0.08], ease: "sine" },
            { t: 0.6, value: [-0.08, 0, -0.08], ease: "sine" },
            { t: 1.2, value: [0, 0, 0.08], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
